import { useState } from 'react';
import { ArrowLeft, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from '@tanstack/react-router';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { POSTAL_CODE_MAX_LENGTH, sanitizePostalCodeInput } from '@/lib/postal-code';
import { FormSectionCard } from '@/components/molecules';
import { AddressSelector } from '@/components/organisms';
import { CompanyLocationSelector } from '@/components/organisms/CompanyLocationSelector';
import { BillToSelector } from '@/components/organisms/contacts/BillToSelector';
import { EstimateLiteLineItemsSection } from '@/components/organisms/estimates/EstimateLiteLineItemsSection';
import { TaxResolutionSection } from '@/components/tax/TaxResolutionSection';
import { useCreateEstimate } from '@/hooks/estimates/useCreateEstimate';
import { useEstimateCustomerState } from '@/hooks/estimates/useEstimateCustomerState';
import { useEstimateCustomerTaxSync } from '@/hooks/estimates/useEstimateCustomerTaxSync';
import { useEstimateLiteLineItems } from '@/hooks/estimates/useEstimateLiteLineItems';
import { useEstimatePrefillContact } from '@/hooks/estimates/useEstimatePrefillContact';
import { useEstimateTaxResolution } from '@/hooks/estimates/useEstimateTaxResolution';
import { useSendEstimate } from '@/hooks/estimates/useSendEstimate';
import { buildEstimateLitePayload } from './_shared';

export function QuickEstimateForm({ prefillContactId }: { prefillContactId?: string }) {
  const router = useRouter();
  const createEstimate = useCreateEstimate();
  const sendEstimate = useSendEstimate();

  const {
    contactId,
    billToId,
    setBillToId,
    customerName,
    customerEmail,
    customerPhone,
    primaryContactName,
    primaryContactTitle,
    contactAddresses,
    selectedAddressIndex,
    setSelectedAddressIndex,
    showAllAddresses,
    setShowAllAddresses,
    saStreet,
    setSaStreet,
    saCity,
    setSaCity,
    saState,
    setSaState,
    saZip,
    setSaZip,
    applyAddress,
    selectContact,
    clearContact,
  } = useEstimateCustomerState();

  const {
    taxResolution,
    taxLoading,
    resolveTax,
    handleZipChange,
    clearPendingTaxResolution,
    setTaxResolution,
  } = useEstimateTaxResolution({ city: saCity, state: saState, zipDebounceMs: 600 });

  const { applyAddressWithTax, selectContactWithTax, clearContactWithTax } =
    useEstimateCustomerTaxSync({
      applyAddress,
      selectContact,
      clearContact,
      resolveTax,
      clearPendingTaxResolution,
      setTaxResolution,
    });

  const [saving, setSaving] = useState(false);

  useEstimatePrefillContact({
    prefillContactId,
    isEditMode: false,
    onSelectContact: selectContactWithTax,
    isCustomerAlreadySet: () => Boolean(contactId),
  });

  const {
    lineItems,
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    showSearchResults,
    setShowSearchResults,
    searchRef,
    addItem,
    removeLineItem,
    updateQuantity,
    lineItemPreviews,
    subtotal,
    taxTotal,
    taxBreakdown,
    grandTotal,
  } = useEstimateLiteLineItems(taxResolution);

  async function handleSave() {
    if (!contactId) {
      toast.error('Please select a customer');
      return;
    }
    if (lineItems.length === 0) {
      toast.error('Add at least one line item');
      return;
    }
    setSaving(true);
    try {
      const payload = buildEstimateLitePayload({
        contactId,
        billToId,
        customerName,
        customerEmail,
        customerPhone,
        primaryContactName,
        primaryContactTitle,
        saStreet,
        saCity,
        saState,
        saZip,
        lineItems,
      });
      const created = (await createEstimate.mutateAsync(payload)) as { id?: string; _id?: string };
      const newId = created?.id ?? created?._id;
      if (newId) {
        // @ts-expect-error — search required by TanStack Router v1.168
        void router.navigate({
          to: '/dashboard/estimates/$id',
          params: { id: newId },
        });
      } else {
        // @ts-expect-error — search required by TanStack Router v1.168
        void router.navigate({ to: '/dashboard/estimates' });
      }
    } catch {
      // The mutation hook has already toasted the server's reason; catching
      // here only stops the navigation and clears the saving state.
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAndSend() {
    if (!contactId) {
      toast.error('Please select a customer');
      return;
    }
    if (lineItems.length === 0) {
      toast.error('Add at least one line item');
      return;
    }
    setSaving(true);
    try {
      const payload = buildEstimateLitePayload({
        contactId,
        billToId,
        customerName,
        customerEmail,
        customerPhone,
        primaryContactName,
        primaryContactTitle,
        saStreet,
        saCity,
        saState,
        saZip,
        lineItems,
      });
      const created = (await createEstimate.mutateAsync(payload)) as { id?: string; _id?: string };
      const newId = created?.id ?? created?._id;
      if (!newId) {
        // @ts-expect-error — search required by TanStack Router v1.168
        void router.navigate({ to: '/dashboard/estimates' });
        return;
      }
      // Two steps, one button — and the second can fail on its own (no email
      // or phone on the contact, a delivery error). The estimate EXISTS either
      // way, so land on it regardless: staying on the form after a failed send
      // is what made a delivery problem read as "the system did not create my
      // estimate". The hook has already toasted the reason, and the toast
      // survives the navigation.
      try {
        await sendEstimate.mutateAsync({ id: newId });
      } finally {
        // @ts-expect-error — search required by TanStack Router v1.168
        void router.navigate({
          to: '/dashboard/estimates/$id',
          params: { id: newId },
        });
      }
    } catch {
      // Already toasted by the create/send hook — this only stops the flow.
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="p-4 sm:p-6 space-y-5 max-w-2xl mx-auto w-full pb-10">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            // @ts-expect-error — search required by TanStack Router v1.168
            onClick={() => void router.navigate({ to: '/dashboard/estimates/new' })}
          >
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">Quick Estimate</h1>
            <p className="text-sm text-muted-foreground">
              Fill in the essentials and save as draft
            </p>
          </div>
        </div>

        {/* Customer */}
        <FormSectionCard className="space-y-3">
          <p className="font-semibold text-sm">
            Customer <span className="text-destructive">*</span>
          </p>
          <CompanyLocationSelector
            selected={
              contactId
                ? {
                    id: contactId,
                    name: customerName,
                    primaryEmail: customerEmail || undefined,
                    primaryPhone: customerPhone || undefined,
                    primaryContactName: primaryContactName || undefined,
                    primaryContactTitle: primaryContactTitle || undefined,
                  }
                : null
            }
            onSelect={selectContactWithTax}
            onClear={clearContactWithTax}
            placeholder="Search customer..."
          />

          {contactId ? (
            <BillToSelector contactId={contactId} value={billToId} onChange={setBillToId} />
          ) : null}
        </FormSectionCard>

        {/* Service Address */}
        <FormSectionCard className="space-y-3">
          <p className="font-semibold text-sm">
            Service Address <span className="text-destructive">*</span>
          </p>

          <AddressSelector
            title="Service Address"
            mode={contactAddresses.length > 0 ? 'select' : 'manual'}
            addresses={contactAddresses}
            visibleAddresses={showAllAddresses ? contactAddresses : contactAddresses.slice(0, 4)}
            selectedIndex={selectedAddressIndex}
            contactSelected={Boolean(contactId)}
            showAll={showAllAddresses}
            maxVisibleAddresses={4}
            emptyContactMessage="Select a customer first to see their addresses."
            emptyAddressMessage="This contact has no saved addresses. Enter one manually."
            onToggleMode={() => {}}
            onSelectAddress={(index, address) => {
              setSelectedAddressIndex(index);
              applyAddressWithTax(address);
            }}
            onToggleShowAll={() => setShowAllAddresses((value) => !value)}
            renderManual={
              <div className="space-y-2">
                <Input
                  placeholder="Street"
                  value={saStreet}
                  onChange={(event) => setSaStreet(event.target.value)}
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="City"
                    value={saCity}
                    onChange={(event) => setSaCity(event.target.value)}
                  />
                  <Input
                    placeholder="ST"
                    maxLength={2}
                    value={saState}
                    onChange={(event) => setSaState(event.target.value.toUpperCase().slice(0, 2))}
                  />
                  <Input
                    placeholder="ZIP / Postal"
                    maxLength={POSTAL_CODE_MAX_LENGTH}
                    value={saZip}
                    onChange={(event) =>
                      handleZipChange(sanitizePostalCodeInput(event.target.value), setSaZip)
                    }
                  />
                </div>
              </div>
            }
          />

          {(taxLoading || taxResolution) && (
            <TaxResolutionSection resolution={taxResolution} loading={taxLoading} />
          )}
        </FormSectionCard>

        <EstimateLiteLineItemsSection
          lineItems={lineItems}
          lineItemPreviews={lineItemPreviews}
          searchQuery={searchQuery}
          searchResults={searchResults}
          searchLoading={searchLoading}
          showSearchResults={showSearchResults}
          searchRef={searchRef}
          subtotal={subtotal}
          taxTotal={taxTotal}
          taxBreakdown={taxBreakdown}
          grandTotal={grandTotal}
          taxResolved={Boolean(taxResolution)}
          zipValue={saZip}
          onSearchQueryChange={setSearchQuery}
          onSearchFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
          onAddItem={addItem}
          onRemoveItem={removeLineItem}
          onUpdateQuantity={updateQuantity}
        />

        {/* Save */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            disabled={saving || !contactId || lineItems.length === 0}
            onClick={handleSave}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save as Draft
          </Button>
          <Button
            className="flex-1"
            disabled={saving || !contactId || lineItems.length === 0}
            onClick={handleSaveAndSend}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Save & Send
          </Button>
        </div>
      </div>
    </div>
  );
}
